/**
 * KNK-1.12 / FR-22 — add or edit one Disposition and its rules.
 *
 * The code is shown and never editable: a rename changes the name only, so every Knock, report
 * and history row keyed on the code keeps lining up. Validation is `validateDraft`, which mirrors
 * the database CHECKs; the database still refuses anything this lets through.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  DISPOSITIONS_COPY as COPY,
  DISPOSITION_CATEGORIES,
  DOOR_FACTS,
  codeFromName,
  draftFrom,
  validateDraft,
  type Disposition,
  type DispositionCategory,
  type DispositionDraft,
  type DispositionPatch,
  type DoorFact,
  type DraftField,
} from '@/lib/knockDispositions';

const NO_FACT = '__none__';

export interface DispositionDialogProps {
  open: boolean;
  /** Undefined adds a new Disposition. */
  disposition?: Disposition;
  existingCodes: readonly string[];
  onOpenChange: (open: boolean) => void;
  onSave: (patch: DispositionPatch, newCode: string | null) => Promise<void>;
}

export function DispositionDialog({ open, disposition, existingCodes, onOpenChange, onSave }: DispositionDialogProps) {
  const [draft, setDraft] = useState<DispositionDraft>(() => draftFrom(disposition));
  const [errors, setErrors] = useState<Partial<Record<DraftField, string>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(draftFrom(disposition));
      setErrors({});
    }
  }, [open, disposition]);

  const set = <K extends DraftField>(key: K, value: DispositionDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const code = disposition ? disposition.code : draft.name.trim() ? codeFromName(draft.name, existingCodes) : '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validateDraft(draft);
    if (!v.ok) {
      setErrors(v.errors);
      return;
    }
    setSaving(true);
    try {
      await onSave(v.patch, disposition ? null : code);
      onOpenChange(false);
    } catch {
      // The caller has already shown the server's sentence; stay open so nothing typed is lost.
    } finally {
      setSaving(false);
    }
  };

  const field = (key: DraftField, label: string, input: React.ReactNode, hint?: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={`disp-${key}`}>{label}</Label>
      {input}
      {errors[key] ? (
        <p className="text-xs text-destructive">{errors[key]}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{disposition ? `Edit ${disposition.name}` : COPY.add}</DialogTitle>
            <DialogDescription>
              {code ? (
                <>
                  Code <code className="rounded bg-muted px-1 py-0.5 text-xs">{code}</code>
                  {disposition ? ' never changes.' : ' is fixed once saved.'}
                </>
              ) : (
                'The code is made from the name and fixed once saved.'
              )}
            </DialogDescription>
          </DialogHeader>

          {field('name', COPY.fields.name,
            <Input id="disp-name" value={draft.name} maxLength={40} onChange={(e) => set('name', e.target.value)} />)}

          <div className="grid gap-4 sm:grid-cols-2">
            {field('category', COPY.fields.category,
              <Select value={draft.category} onValueChange={(v) => set('category', v as DispositionCategory)}>
                <SelectTrigger id="disp-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISPOSITION_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>,
              draft.category === 'Do Not Knock' ? COPY.dnkNote : undefined)}
            {field('color', COPY.fields.color,
              <div className="flex gap-2">
                <input
                  type="color"
                  aria-label="Pick a colour"
                  value={/^#[0-9A-Fa-f]{6}$/.test(draft.color) ? draft.color : '#000000'}
                  onChange={(e) => set('color', e.target.value.toUpperCase())}
                  className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
                />
                <Input id="disp-color" value={draft.color} maxLength={7} onChange={(e) => set('color', e.target.value)} />
              </div>)}
          </div>

          <ToggleRow id="disp-requiresNote" label={COPY.fields.requiresNote} hint={COPY.fields.requiresNoteHint}
            checked={draft.requiresNote} onChange={(v) => set('requiresNote', v)} />
          <ToggleRow id="disp-createsLead" label={COPY.fields.createsLead} hint={COPY.fields.createsLeadHint}
            checked={draft.createsLead} onChange={(v) => set('createsLead', v)} />

          <div className="grid gap-4 sm:grid-cols-2">
            {field('revisitOffsetHours', COPY.fields.revisitOffsetHours,
              <Input id="disp-revisitOffsetHours" inputMode="numeric" value={draft.revisitOffsetHours}
                onChange={(e) => set('revisitOffsetHours', e.target.value)} />,
              COPY.fields.revisitOffsetHint)}
            {field('terminalDays', COPY.fields.terminalDays,
              <Input id="disp-terminalDays" inputMode="numeric" value={draft.terminalDays}
                onChange={(e) => set('terminalDays', e.target.value)} />,
              COPY.fields.terminalHint)}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {field('doorFact', COPY.fields.doorFact,
              <Select value={draft.doorFact || NO_FACT}
                onValueChange={(v) => setDraft((d) => ({
                  ...d,
                  doorFact: v === NO_FACT ? '' : (v as DoorFact),
                  doorFactDays: v === NO_FACT ? '' : d.doorFactDays,
                }))}>
                <SelectTrigger id="disp-doorFact"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_FACT}>{COPY.fields.doorFactNone}</SelectItem>
                  {DOOR_FACTS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>)}
            {field('doorFactDays', COPY.fields.doorFactDays,
              <Input id="disp-doorFactDays" inputMode="numeric" value={draft.doorFactDays} disabled={draft.doorFact === ''}
                onChange={(e) => set('doorFactDays', e.target.value)} />,
              COPY.fields.doorFactDaysHint)}
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ id, label, hint, checked, onChange }: {
  id: string; label: string; hint: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
      <div className="space-y-0.5">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
